<template>
  <div class="politicas-cobrancas-page">
    <div
      class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3"
    >
      <div>
        <h5 class="mb-1">
          Políticas de Cobrança
        </h5>

        <small class="text-muted">
          Consulte regras de vencimento, pró-rata, desconto, multa e juros.
        </small>
      </div>

      <div class="d-flex align-items-center gap-2">
        <button
          v-if="podeConfigurar"
          type="button"
          class="btn btn-primary btn-sm"
          :disabled="loading"
          @click="novaPolitica"
        >
          <i class="bi bi-plus-circle me-1"></i>
          Nova política
        </button>

        <button
          type="button"
          class="btn btn-outline-secondary btn-sm"
          :disabled="loading"
          @click="carregarPoliticas"
        >
          <i class="bi bi-arrow-clockwise me-1"></i>
          Atualizar
        </button>
      </div>
    </div>

    <div class="card shadow-sm mb-3">
      <div class="card-body">
        <div class="filters-grid">
          <div class="filter-wide">
            <label class="form-label">
              Busca
            </label>

            <input
              v-model.trim="filters.busca"
              type="search"
              class="form-control form-control-sm"
              placeholder="Nome da política"
              @keyup.enter="aplicarFiltros"
            />
          </div>

          <div>
            <label class="form-label">
              Situação
            </label>

            <select
              v-model="filters.situacao"
              class="form-select form-select-sm"
              @change="aplicarFiltros"
            >
              <option value="">
                Todas
              </option>

              <option value="RASCUNHO">
                Rascunho
              </option>

              <option value="PUBLICADA">
                Publicada
              </option>

              <option value="CANCELADA">
                Cancelada
              </option>
            </select>
          </div>

          <div>
            <label class="form-label">
              Situação efetiva
            </label>

            <select
              v-model="filters.situacao_efetiva"
              class="form-select form-select-sm"
              @change="aplicarFiltros"
            >
              <option value="">
                Todas
              </option>

              <option value="RASCUNHO">
                Rascunho
              </option>

              <option value="AGENDADA">
                Agendada
              </option>

              <option value="VIGENTE">
                Vigente
              </option>

              <option value="ENCERRADA">
                Encerrada
              </option>

              <option value="CANCELADA">
                Cancelada
              </option>
            </select>
          </div>

          <div class="filter-actions">
            <button
              type="button"
              class="btn btn-primary btn-sm me-2"
              :disabled="loading"
              @click="aplicarFiltros"
            >
              <i class="bi bi-search me-1"></i>
              Buscar
            </button>

            <button
              type="button"
              class="btn btn-secondary btn-sm"
              :disabled="loading"
              @click="limparFiltros"
            >
              <i class="bi bi-funnel me-1"></i>
              Limpar
            </button>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="errorMessage"
      class="alert alert-danger"
      role="alert"
    >
      {{ errorMessage }}
    </div>

    <div class="card shadow-sm">
      <div class="card-body p-0">
        <div
          v-if="loading"
          class="py-5 text-center"
        >
          <div
            class="spinner-border spinner-border-sm text-primary"
            role="status"
          ></div>

          <div class="mt-2 text-muted">
            Carregando políticas...
          </div>
        </div>

        <div
          v-else-if="items.length === 0"
          class="py-5 text-center text-muted"
        >
          <i class="bi bi-sliders fs-3 d-block mb-2"></i>
          Nenhuma política de cobrança encontrada.
        </div>

        <div
          v-else
          class="table-responsive"
        >
          <table
            class="table table-striped table-hover align-middle mb-0"
          >
            <thead class="table-light">
              <tr>
                <th>ID</th>
                <th>Nome</th>
                <th>Situação</th>
                <th>Vigência</th>
                <th>Dias de vencimento</th>
                <th>Pró-rata</th>
                <th>Desconto antecipado</th>
                <th>Multa</th>
                <th>Juros</th>
                <th class="text-center">
                  Ações
                </th>
              </tr>
            </thead>

            <tbody>
              <tr
                v-for="item in items"
                :key="item.id"
              >
                <td class="fw-semibold text-nowrap">
                  #{{ item.id }}
                </td>

                <td>
                  {{ item.nome }}
                </td>

                <td class="text-nowrap">
                  <span
                    class="status-badge"
                    :class="statusClass(item.situacao_efetiva)"
                  >
                    {{ statusLabel(item.situacao_efetiva) }}
                  </span>

                  <small
                    v-if="item.situacao !== item.situacao_efetiva"
                    class="d-block text-muted mt-1"
                  >
                    Registro: {{ statusLabel(item.situacao) }}
                  </small>
                </td>

                <td class="text-nowrap">
                  {{ formatVigencia(item) }}
                </td>

                <td>
                  {{ formatDias(item.dias_vencimento) }}
                </td>

                <td>
                  {{ formatProrata(item) }}
                </td>

                <td>
                  {{ formatDesconto(item) }}
                </td>

                <td>
                  {{ formatMulta(item) }}
                </td>

                <td>
                  {{ formatJuros(item) }}
                </td>

                <td class="text-center text-nowrap">
                  <button
                    type="button"
                    class="btn btn-link btn-sm p-1"
                    title="Detalhar política"
                    :aria-label="`Detalhar política #${item.id}`"
                    @click="detalharPolitica(item.id)"
                  >
                    <i class="bi bi-eye-fill text-primary"></i>
                  </button>

                  <button
                    v-if="podeEditarItem(item)"
                    type="button"
                    class="btn btn-link btn-sm p-1"
                    title="Editar rascunho"
                    :aria-label="`Editar política #${item.id}`"
                    @click="editarPolitica(item.id)"
                  >
                    <i class="bi bi-pencil-fill text-warning"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

    </div>

    <div
      v-if="!loading && paginacao.total_itens > 0"
      class="d-flex flex-wrap justify-content-between align-items-center gap-3 mt-3"
    >
      <div>
        Mostrando
        {{ startRow }}
        a
        {{ endRow }}
        de
        {{ paginacao.total_itens }}
        políticas
      </div>

      <div class="d-flex align-items-center gap-2">
        <BFormSelect
          v-model="limit"
          :options="pageOptions"
          size="sm"
          class="limit-select"
          aria-label="Itens por página"
        />
      </div>

      <div class="d-flex align-items-center">
        <BPagination
          v-model="page"
          :per-page="limit"
          :total-rows="paginacao.total_itens"
          size="sm"
          :limit="5"
          class="mb-0"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  computed,
  onMounted,
  reactive,
  ref,
  watch
} from 'vue';

import {
  useRouter
} from 'vue-router';

import {
  BFormSelect,
  BPagination
} from 'bootstrap-vue-next';

import {
  usePermissions
} from '@/composables/usePermissions';

import politicasCobrancasApi
  from '@/services/politicasCobrancas.api';

const {
  fetchPermissions,
  hasPermission
} = usePermissions();

const router =
  useRouter();

const items =
  ref([]);

const loading =
  ref(false);

const errorMessage =
  ref('');

const page =
  ref(1);

const limit =
  ref(20);

const pageOptions = [
  {
    value: 10,
    text: '10 / página'
  },
  {
    value: 15,
    text: '15 / página'
  },
  {
    value: 20,
    text: '20 / página'
  },
  {
    value: 25,
    text: '25 / página'
  },
  {
    value: 50,
    text: '50 / página'
  }
];

const filters =
  reactive({
    busca: '',
    situacao: '',
    situacao_efetiva: ''
  });

const paginacao =
  reactive({
    pagina: 1,
    limite: 20,
    total_itens: 0,
    total_paginas: 0
  });

const podeConfigurar =
  computed(() =>
    hasPermission(
      'financeiro_cobrancas_configurar'
    )
  );

const startRow =
  computed(() => {
    if (
      paginacao.total_itens === 0
    ) {
      return 0;
    }

    return (
      (page.value - 1) *
        limit.value +
      1
    );
  });

const endRow =
  computed(() =>
    Math.min(
      page.value *
        limit.value,
      paginacao.total_itens
    )
  );

function apiErrorMessage(
  error,
  fallback
) {
  return (
    error?.response?.data?.message ||
    fallback
  );
}

function formatDate(
  value
) {
  if (
    typeof value !== 'string' ||
    !/^\d{4}-\d{2}-\d{2}$/.test(
      value
    )
  ) {
    return '—';
  }

  const [
    year,
    month,
    day
  ] = value.split('-');

  return `${day}/${month}/${year}`;
}

function formatDecimal(
  value
) {
  if (
    value === null ||
    value === undefined ||
    value === ''
  ) {
    return '—';
  }

  const raw =
    String(value).trim();

  if (
    !/^\d+(?:\.\d+)?$/.test(
      raw
    )
  ) {
    return raw;
  }

  const [
    integer,
    decimals = ''
  ] = raw.split('.');

  const trimmed =
    decimals.replace(
      /0+$/,
      ''
    );

  return trimmed
    ? `${integer},${trimmed}`
    : integer;
}

function formatVigencia(
  item
) {
  const inicio =
    formatDate(
      item?.vigencia_inicio
    );

  const fim =
    item?.vigencia_fim
      ? formatDate(
          item.vigencia_fim
        )
      : 'sem término';

  return `${inicio} até ${fim}`;
}

function formatDias(
  dias
) {
  if (
    !Array.isArray(dias) ||
    dias.length === 0
  ) {
    return 'Nenhum';
  }

  return [
    ...dias
  ]
    .sort(
      (a, b) =>
        Number(a?.dia) -
        Number(b?.dia)
    )
    .map(
      (item) =>
        item?.padrao
          ? `${item.dia} (padrão)`
          : String(item?.dia ?? '')
    )
    .filter(Boolean)
    .join(', ');
}

function formatProrata(
  item
) {
  if (
    !item?.prorata_habilitado
  ) {
    return 'Não';
  }

  if (
    item.prorata_modo ===
    'PRIMEIRA_PARCELA'
  ) {
    return 'Sim — primeira parcela';
  }

  if (
    item.prorata_modo ===
    'ACUMULAR_PROXIMA_PARCELA'
  ) {
    return 'Sim — acumular na próxima';
  }

  return 'Sim';
}

function formatDesconto(
  item
) {
  if (
    !item
      ?.desconto_antecipacao_habilitado
  ) {
    return 'Não';
  }

  const percentual =
    formatDecimal(
      item
        .desconto_antecipacao_percentual
    );

  const dias =
    item
      .desconto_antecipacao_dias;

  return (
    `${percentual}% — ` +
    `${dias} dia(s) antes`
  );
}

function formatMulta(
  item
) {
  if (
    !item?.multa_habilitada
  ) {
    return 'Não';
  }

  return (
    `${formatDecimal(
      item.multa_percentual
    )}%`
  );
}

function formatJuros(
  item
) {
  if (item?.sicredi_juros_percentual_dia != null) return `${formatDecimal(item.sicredi_juros_percentual_dia)}% ao dia (SICREDI; início pendente de confirmação)`;
  if (item?.sicredi_juros_valor_dia != null) return `R$ ${formatDecimal(item.sicredi_juros_valor_dia)} por dia (SICREDI; início não especificado)`;
  if (
    !item?.juros_habilitado
  ) {
    return 'Não';
  }

  return (
    `${formatDecimal(
      item.juros_percentual_mes
    )}% ao mês`
  );
}

function statusLabel(
  status
) {
  const labels = {
    RASCUNHO:
      'Rascunho',

    PUBLICADA:
      'Publicada',

    AGENDADA:
      'Agendada',

    VIGENTE:
      'Vigente',

    ENCERRADA:
      'Encerrada',

    CANCELADA:
      'Cancelada'
  };

  return (
    labels[
      String(
        status || ''
      ).toUpperCase()
    ] ||
    status ||
    '—'
  );
}

function statusClass(
  status
) {
  const classes = {
    RASCUNHO:
      'status-secondary',

    PUBLICADA:
      'status-primary',

    AGENDADA:
      'status-info',

    VIGENTE:
      'status-success',

    ENCERRADA:
      'status-dark',

    CANCELADA:
      'status-danger'
  };

  return (
    classes[
      String(
        status || ''
      ).toUpperCase()
    ] ||
    'status-secondary'
  );
}

function novaPolitica() {
  router.push({
    name:
      'NovaPoliticaCobranca'
  });
}

function podeEditarItem(
  item
) {
  return (
    podeConfigurar.value &&
    item?.situacao ===
      'RASCUNHO'
  );
}

function editarPolitica(
  id
) {
  const normalizedId =
    Number(id);

  if (
    !Number.isSafeInteger(
      normalizedId
    ) ||
    normalizedId <= 0
  ) {
    return;
  }

  router.push({
    name:
      'EditarPoliticaCobranca',

    params: {
      id:
        normalizedId
    }
  });
}

function detalharPolitica(
  id
) {
  const normalizedId =
    Number(id);

  if (
    !Number.isSafeInteger(
      normalizedId
    ) ||
    normalizedId <= 0
  ) {
    return;
  }

  router.push({
    name:
      'DetalharPoliticaCobranca',

    params: {
      id:
        normalizedId
    }
  });
}

async function carregarPoliticas() {
  if (
    !hasPermission(
      'financeiro_cobrancas_visualizar'
    )
  ) {
    items.value = [];

    errorMessage.value =
      'Você não possui permissão para visualizar políticas de cobrança.';

    return;
  }

  loading.value =
    true;

  errorMessage.value =
    '';

  try {
    const result =
      await politicasCobrancasApi
        .listar({
          pagina:
            page.value,

          limite:
            limit.value,

          busca:
            filters.busca,

          situacao:
            filters.situacao,

          situacao_efetiva:
            filters
              .situacao_efetiva
        });

    items.value =
      result.data;

    Object.assign(
      paginacao,
      result.paginacao
    );
  } catch (error) {
    console.error(
      'Erro ao carregar políticas de cobrança:',
      error
    );

    items.value = [];

    errorMessage.value =
      apiErrorMessage(
        error,
        'Não foi possível carregar as políticas de cobrança.'
      );
  } finally {
    loading.value =
      false;
  }
}

async function aplicarFiltros() {
  if (page.value !== 1) {
    page.value = 1;
    return;
  }

  await carregarPoliticas();
}

async function limparFiltros() {
  filters.busca = '';
  filters.situacao = '';
  filters.situacao_efetiva = '';

  if (page.value !== 1) {
    page.value = 1;
    return;
  }

  await carregarPoliticas();
}

watch(
  page,
  () => {
    carregarPoliticas();
  }
);

watch(
  limit,
  () => {
    if (page.value !== 1) {
      page.value = 1;
      return;
    }

    carregarPoliticas();
  }
);

onMounted(
  async () => {
    await fetchPermissions();

    await carregarPoliticas();
  }
);
</script>

<style scoped>
.politicas-cobrancas-page {
  font-size: 0.82rem;
}

.filters-grid {
  display: grid;
  grid-template-columns:
    minmax(220px, 2fr)
    minmax(150px, 1fr)
    minmax(170px, 1fr)
    auto;
  gap: 12px;
  align-items: end;
}

.filter-actions {
  display: flex;
  align-items: end;
  white-space: nowrap;
}

.form-label {
  font-size: 0.75rem;
  margin-bottom: 0.2rem;
}

.status-badge {
  display: inline-block;
  padding: 0.2rem 0.45rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  white-space: nowrap;
}

.status-secondary {
  background: #e9ecef;
  color: #495057;
}

.status-primary {
  background: #cfe2ff;
  color: #084298;
}

.status-info {
  background: #cff4fc;
  color: #055160;
}

.status-success {
  background: #d1e7dd;
  color: #0f5132;
}

.status-dark {
  background: #dee2e6;
  color: #212529;
}

.status-danger {
  background: #f8d7da;
  color: #842029;
}

.limit-select {
  width: 6rem;
}

:deep(.pagination.pagination-sm .page-link) {
  line-height: 1;
  font-size: 0.8rem !important;
  border-radius: 0 !important;
}

.table th,
.table td {
  font-size: 0.77rem;
  vertical-align: middle;
}

@media (max-width: 1100px) {
  .filters-grid {
    grid-template-columns:
      repeat(
        2,
        minmax(0, 1fr)
      );
  }

  .filter-actions {
    grid-column: 1 / -1;
  }
}

@media (max-width: 700px) {
  .filters-grid {
    grid-template-columns: 1fr;
  }

  .filter-actions {
    grid-column: auto;
  }
}
</style>
